import { useState, useEffect } from "react";
import { normalizeUrl } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { memberCategories } from "@/data/members";
import ImageUploadWithCrop from "@/components/shared/ImageUploadWithCrop";
import { Pencil, Globe, Phone, Mail, Linkedin, Facebook, Instagram } from "lucide-react";
import SnapchatIcon from "@/components/icons/SnapchatIcon";
import { format } from "date-fns";

interface ProfileFormProps {
  userId: string;
}

const ProfileForm = ({ userId }: ProfileFormProps) => {
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  const [memberData, setMemberData] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    title: "",
    description: "",
    about: "",
    website: "",
    phone: "",
    email: "",
    category: "",
    image: "",
    linkedin: "",
    facebook: "",
    instagram: "",
    snapchat: "",
  });
  const [joinedDate, setJoinedDate] = useState<string | null>(null);

  useEffect(() => {
    fetchMemberProfile();
  }, [userId]);

  const fetchMemberProfile = async () => {
    try {
      const { data, error } = await supabase
        .from("members")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setMemberData(data);
        setJoinedDate(data.joined_date || null);
        setFormData({
          name: data.name || "",
          company: data.company || "",
          title: data.title || "",
          description: data.description || "",
          about: data.about || "",
          website: data.website || "",
          phone: data.phone || "",
          email: data.email || "",
          category: data.category || "",
          image: data.image || "",
          linkedin: data.linkedin || "",
          facebook: data.facebook || "",
          instagram: data.instagram || "",
          snapchat: data.snapchat || "",
        });
        setEditing(false);
      } else {
        // No profile yet — go straight to edit mode
        setEditing(true);
      }
    } catch (error: any) {
      toast.error("Error loading profile: " + error.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const memberPayload = {
        user_id: userId,
        ...formData,
        website: normalizeUrl(formData.website),
        linkedin: normalizeUrl(formData.linkedin),
        facebook: normalizeUrl(formData.facebook),
      };

      if (memberData) {
        const { error } = await supabase
          .from("members")
          .update(memberPayload)
          .eq("user_id", userId);

        if (error) throw error;
        toast.success("Profile updated successfully!");
      } else {
        const { error } = await supabase
          .from("members")
          .insert([memberPayload]);

        if (error) throw error;
        toast.success("Profile created successfully!");
      }

      fetchMemberProfile();
    } catch (error: any) {
      toast.error("Error saving profile: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // --- View Mode ---
  if (!editing && memberData) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Member Profile</CardTitle>
            <CardDescription>Your information in the member directory</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Profile
          </Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Logo & Identity */}
          <div className="flex items-start gap-6">
            {formData.image && (
              <img
                src={formData.image}
                alt={formData.company}
                className="h-24 w-24 rounded-lg object-cover border"
              />
            )}
            <div className="space-y-1">
              <h3 className="text-xl font-semibold">{formData.name}</h3>
              <p className="text-muted-foreground">{formData.title} at {formData.company}</p>
              <p className="text-sm text-muted-foreground">{formData.category}</p>
              {joinedDate && (
                <p className="text-sm text-muted-foreground">
                  Member since {format(new Date(joinedDate + "T00:00:00"), "MMMM yyyy")}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          {formData.description && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Description</Label>
              <p className="mt-1">{formData.description}</p>
            </div>
          )}

          {/* About */}
          {formData.about && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">About</Label>
              <p className="mt-1 whitespace-pre-line">{formData.about}</p>
            </div>
          )}

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {formData.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a href={`mailto:${formData.email}`} className="text-primary hover:underline">{formData.email}</a>
              </div>
            )}
            {formData.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span>{formData.phone}</span>
              </div>
            )}
            {formData.website && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <a href={normalizeUrl(formData.website) ?? undefined} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">{formData.website}</a>
              </div>
            )}
            {formData.linkedin && (
              <div className="flex items-center gap-2 text-sm">
                <Linkedin className="h-4 w-4 text-muted-foreground" />
                <a href={normalizeUrl(formData.linkedin) ?? undefined} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">LinkedIn</a>
              </div>
            )}
            {formData.facebook && (
              <div className="flex items-center gap-2 text-sm">
                <Facebook className="h-4 w-4 text-muted-foreground" />
                <a href={normalizeUrl(formData.facebook) ?? undefined} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">Facebook</a>
              </div>
            )}
            {formData.instagram && (
              <div className="flex items-center gap-2 text-sm">
                <Instagram className="h-4 w-4 text-muted-foreground" />
                <a href={formData.instagram} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">Instagram</a>
              </div>
            )}
            {formData.snapchat && (
              <div className="flex items-center gap-2 text-sm">
                <SnapchatIcon className="h-4 w-4 text-muted-foreground" />
                <a href={formData.snapchat} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline truncate">Snapchat</a>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // --- Edit Mode ---
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{memberData ? "Edit Profile" : "Create Profile"}</CardTitle>
          <CardDescription>
            {memberData
              ? "Update your information in the member directory"
              : "Fill in your information to appear in the member directory"}
          </CardDescription>
        </div>
        {memberData && (
          <Button variant="outline" size="sm" onClick={() => {
            // Reset form data to saved values and exit edit mode
            fetchMemberProfile();
          }}>
            Cancel
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="mb-6">
            <ImageUploadWithCrop
              currentImage={formData.image}
              fallbackText={formData.company}
              userId={userId}
              onImageChange={(url) => handleChange("image", url)}
              label="Company Logo"
            />
          </div>

          {joinedDate && (
            <div className="text-sm text-muted-foreground mb-2">
              Member since {format(new Date(joinedDate + "T00:00:00"), "MMMM yyyy")}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company">Company *</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => handleChange("company", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select value={formData.category} onValueChange={(value) => handleChange("category", value)} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {memberCategories.filter(cat => cat !== "All").map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => handleChange("website", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="linkedin">LinkedIn URL</Label>
              <Input
                id="linkedin"
                type="url"
                value={formData.linkedin}
                onChange={(e) => handleChange("linkedin", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="facebook">Facebook URL</Label>
              <Input
                id="facebook"
                type="url"
                value={formData.facebook}
                onChange={(e) => handleChange("facebook", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="instagram">Instagram URL</Label>
              <Input
                id="instagram"
                type="url"
                value={formData.instagram}
                onChange={(e) => handleChange("instagram", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="snapchat">Snapchat URL</Label>
              <Input
                id="snapchat"
                type="url"
                value={formData.snapchat}
                onChange={(e) => handleChange("snapchat", e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Brief Description *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="about">About (Optional)</Label>
            <Textarea
              id="about"
              value={formData.about}
              onChange={(e) => handleChange("about", e.target.value)}
              rows={4}
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : memberData ? "Update Profile" : "Create Profile"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProfileForm;
